// src/plugins/sales-forecast/components/PriceChangeControl.tsx
import React, { useEffect, useState } from "react";
import { Input } from "../../../components/ui/input";
import { Alert } from "../../../components/ui/alert";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";
import "./PriceChangeControl.css"; // ⬅️ keep arrow visibility

export interface PriceChangeControlProps {
  /** Label shown above the input, e.g. "3-Month Price Change ($)" */
  label: string;
  /** Numeric value to display */
  value: number;
  /** Emits a valid numeric value when changed */
  onChange: (value: number) => void;
  /** Optional bounds and step size */
  min?: number;
  max?: number;
  step?: number;
}

/**
 * A reusable, theme-aware single-field numeric input control.
 * Used for each individual price change (3mo, 6mo, etc.)
 */
export const PriceChangeControl: React.FC<PriceChangeControlProps> = ({
  label,
  value,
  onChange,
  min = -1_000_000,
  max = 1_000_000,
  step = 250,
}) => {
  const theme = useSystemTheme();

  // Pull system theme colors from CSS variables
  const css = getComputedStyle(document.documentElement);
  const textColor = css.getPropertyValue("--text-color").trim();
  const borderColor = css.getPropertyValue("--border-color").trim();
  const inputBg = css.getPropertyValue("--input-bg").trim() || "var(--card-bg)";
  const labelColor =
    css.getPropertyValue("--muted-text-color").trim() || textColor;

  const [internalValue, setInternalValue] = useState<number>(value);
  const [error, setError] = useState<string>("");

  // Keep local state synced with parent
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Validate and emit
  const validateAndEmit = (num: number) => {
    if (num < min) {
      setError(`Must be ≥ ${min}`);
    } else if (num > max) {
      setError(`Must be ≤ ${max}`);
    } else {
      setError("");
      onChange(num);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const cleaned = raw
      .replace(/[^0-9.-]/g, "")
      .replace(/(?!^)-/g, "")
      .replace(/\.{2,}/g, ".")
      .replace(/^(-?)([^.]*)\.(.*)\./, "$1$2.$3");

    const parsed =
      cleaned === "" || cleaned === "-" || cleaned === "." || cleaned === "-."
        ? NaN
        : parseFloat(cleaned);

    if (!isNaN(parsed)) {
      setInternalValue(parsed);
      validateAndEmit(parsed);
    } else {
      setInternalValue(NaN as unknown as number);
    }
  };

  const inputClass =
    "w-full text-center rounded shadow-sm focus:outline-none [appearance:auto] transition-all duration-150";
  const inputStyle = {
    backgroundColor: inputBg,
    color: textColor,
    border: `1px solid ${borderColor}`,
  };

  const labelClass = "block text-sm font-medium mb-1";
  const labelStyle = { color: labelColor };

  return (
    <div
      className={`theme-${theme} space-y-1 transition-all duration-200`}
      style={{ color: textColor }}
    >
      <label className={labelClass} style={labelStyle}>
        {label}
      </label>
      <Input
        type="number"
        value={Number.isNaN(internalValue) ? "" : internalValue}
        onChange={handleChange}
        step={step}
        min={min}
        max={max}
        className={inputClass}
        style={inputStyle}
      />
      {error && <Alert className="mt-1">{error}</Alert>}
    </div>
  );
};